import { Assigned } from '@Models/types';
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from 'react';
import { useAuth } from './CognitoAuthProvider';
import { ahaAPI } from '@Lib/axios/aha';
import { jiraAPI } from '@Lib/axios/jira';

type TicketSource = 'aha' | 'jira' | undefined;

interface TicketCacheState {
  aha: Assigned[];
  jira: Assigned[];
}

interface TicketContextProps {
  tickets: Assigned[];
  fetchTickets: (options?: { force?: boolean }) => Promise<void>;
  selectedTicket: Assigned | null;
  setSelectedTicket: (tickets: Assigned) => void;
  ticketSource: TicketSource;
  setTicketSource: (source: TicketSource) => void;
  isLoading: boolean;
  error: string | null;
  clearCache: () => void;
}

const TicketContext = createContext<TicketContextProps | undefined>(undefined);

export const TicketContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [ticketCache, setTicketCache] = useState<TicketCacheState>({
    aha: [],
    jira: [],
  });

  const [ticketSource, setTicketSource] = useState<TicketSource>('aha');
  const [selectedTicket, setSelectedTicket] = useState<Assigned | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { logout } = useAuth();
  const aha = useMemo(() => ahaAPI(logout), [logout]);
  const jira = useMemo(() => jiraAPI(logout), [logout]);

  const tickets = useMemo(() => {
    if (!ticketSource) return [];
    return ticketCache[ticketSource] || [];
  }, [ticketCache, ticketSource]);

  const clearCache = () => {
    setTicketCache({ aha: [], jira: [] });
  };

  const fetchAllTickets = async (options?: { force?: boolean }) => {
    const hasData = ticketCache.aha.length > 0 || ticketCache.jira.length > 0;

    if (hasData && !options?.force) return;

    setIsLoading(true);
    setError(null);

    try {
      const [ahaResult, jiraResult] = await Promise.allSettled([
        aha.getAssignedRecords(),
        jira.getAssignedRecords(),
      ]);

      const newCache: TicketCacheState = {
        aha: ticketCache.aha,
        jira: ticketCache.jira,
      };

      const errors: string[] = [];

      if (ahaResult.status === 'fulfilled') {
        newCache.aha = ahaResult.value;
      } else {
        console.warn(`Aha fetch failed:`, ahaResult.reason);

        if (newCache.aha.length === 0)
          errors.push(`Failed to load Aha! tickets`);
      }

      if (jiraResult.status === 'fulfilled') {
        newCache.jira = jiraResult.value;
      } else {
        console.warn('Jira fetch failed:', jiraResult.reason);

        if (newCache.jira.length === 0)
          errors.push('Failed to load Jira! tickets');
      }

      setTicketCache(newCache);

      if (errors.length > 0) {
        setError(errors.join(', '));
      }
    } catch (e) {
      console.error('Critical fetch error', e);
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  // Sync selectedTicket with updated tickets array when cache refreshes
  useEffect(() => {
    if (selectedTicket && tickets.length > 0) {
      const updatedTicket = tickets.find(
        (ticket) => ticket.reference_num === selectedTicket.reference_num
      );
      if (
        updatedTicket &&
        updatedTicket.workflow_status?.color !==
          selectedTicket.workflow_status?.color
      ) {
        setSelectedTicket({ ...updatedTicket });
      }
    }
  }, [tickets, selectedTicket]);

  return (
    <TicketContext.Provider
      value={{
        tickets,
        fetchTickets: fetchAllTickets,
        selectedTicket,
        setSelectedTicket,
        ticketSource,
        setTicketSource,
        isLoading,
        error,
        clearCache,
      }}
    >
      {children}
    </TicketContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTicketContext = (): TicketContextProps => {
  const context = useContext(TicketContext);
  if (!context)
    throw new Error(
      'useTicketContext must be used within a TicketContextProvider'
    );
  return context;
};
